import { useRef, useState, type ReactNode } from 'react';

import { DataTable, type Column } from './DataTable';
import { useEncaixePelaLargura } from './useEncaixePelaLargura';

/**
 * Como a tabela se arruma na largura que recebeu: `ampla` e `reunida` sao a tabela
 * fluida (colunas com largura, sem rolar para o lado), com todas as colunas ou com os
 * dados agrupados na segunda linha da celula; `rolante` e a tabela comum, para a tela
 * estreita do celular, em que as colunas secundarias somem por `hideBelow`.
 */
export type EncaixeDaTabela = 'ampla' | 'reunida' | 'rolante';

/** Larguras da area da tabela, em pixels, a partir das quais cada disposicao vale. */
export interface LargurasDaTabela {
  /** Sem ela, a tabela fluida tem uma disposicao so, a reunida. */
  ampla?: number | undefined;
  reunida: number;
}

function encaixadorDe(larguras: LargurasDaTabela) {
  return (largura: number): EncaixeDaTabela => {
    // Antes da primeira medida nao ha largura: nada indica que falta espaco.
    if (largura <= 0) return larguras.ampla ? 'ampla' : 'reunida';
    if (larguras.ampla && largura >= larguras.ampla) return 'ampla';
    return largura >= larguras.reunida ? 'reunida' : 'rolante';
  };
}

/**
 * A tabela que escolhe a disposicao pela largura medida, como a lista de operacoes
 * (listaDeOperacoesLayout.ts) e a de tomadores: nas telas comuns ela nao rola para o
 * lado, e o que nao cabe numa coluna propria vai para a segunda linha da celula.
 *
 * As larguras sao lidas uma vez, na montagem.
 */
export function TabelaAjustavel<T>({
  colunas,
  larguras,
  items,
  keyOf,
  loading,
  empty,
  onRowClick,
}: {
  colunas: (encaixe: EncaixeDaTabela) => Column<T>[];
  larguras: LargurasDaTabela;
  items: T[];
  keyOf: (item: T) => string;
  loading?: boolean | undefined;
  empty?: ReactNode | undefined;
  onRowClick?: ((item: T) => void) | undefined;
}) {
  const area = useRef<HTMLDivElement>(null);
  const [encaixar] = useState(() => encaixadorDe(larguras));
  const encaixe = useEncaixePelaLargura(area, encaixar);
  const fluida = encaixe !== 'rolante';
  // Na tabela fluida a escolha das colunas vem da largura medida: a coluna escondida por
  // `hideBelow` deixaria a largura dela ocupando a tabela (DataTable).
  const colunasDoEncaixe = fluida
    ? colunas(encaixe).map((coluna) => ({ ...coluna, hideBelow: undefined }))
    : colunas(encaixe);
  return (
    <div ref={area}>
      <DataTable
        fluida={fluida}
        columns={colunasDoEncaixe}
        items={items}
        keyOf={keyOf}
        loading={loading}
        empty={empty}
        onRowClick={onRowClick}
      />
    </div>
  );
}
